import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import SiteNavbar from '../components/SiteNavbar';
import { waitlistDetailsSchema, WaitlistDetails } from './waitlistSchemas';
import { useRegistrationStatus } from '../hooks/useRegistrationStatus';
import { supabase } from '../lib/supabaseClient';
import { ensureFrontendSessionId } from '../lib/session';

/**
 * SignupPage – collects basic user information then sends an OTP to the
 * provided e-mail.  The actual verification step lives at `/auth/verify` and
 * is shared by both the sign-up and login flows.
 */

export default function SignupPage() {
  const navigate = useNavigate();

  const { waitlist } = useRegistrationStatus();

  const [sending, setSending] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<WaitlistDetails>({ resolver: zodResolver(waitlistDetailsSchema) });

  // Detect plus-addressing so we can warn the user (e.g. john+tag@example.com)
  const emailVal = watch('email') ?? '';
  const plusAddressing = /^[^@]+\+[^@]+@[^@]+$/.test(emailVal.trim());

  const onSubmit = async (values: WaitlistDetails) => {
    setSending(true);

    // Guarantee a valid anonymous session so the Edge Function accepts the
    // request even when the user submits the form immediately after a fresh
    // page load.
    const sessionId = await ensureFrontendSessionId();

    const payload = {
      ...values,
      sessionId,
    } as Record<string, unknown>;

    const fn = waitlist ? 'waitlist_request_otp' : 'auth_request_otp';

    const { error } = await supabase.functions.invoke(fn, {
      body: payload,
    });

    Sentry.addBreadcrumb({
      category: 'auth',
      message: `${fn}`,
      level: 'info',
      data: { email: values.email, ok: !error },
    });

    setSending(false);

    if (error) {
      alert(error.message);
      return;
    }

    // Redirect to unified OTP verification page.  We pass `context=signup` so
    // the page knows which backend endpoint to call.
    const ctx = waitlist ? 'waitlist' : 'signup';
    navigate(`/auth/verify?email=${encodeURIComponent(values.email)}&context=${ctx}`);
  };

  return (
    <div className="flex min-h-screen flex-col bg-white dark:bg-gray-900">
      <SiteNavbar />

      <main className="flex flex-1 items-center justify-center px-4 py-16">
        <div className="w-full max-w-lg rounded-lg bg-gray-50 p-8 shadow-md dark:bg-gray-800">
          <h1 className="mb-6 text-center text-3xl font-bold text-gray-900 dark:text-gray-50">
            {waitlist ? 'Join the wait-list' : 'Create your free account'}
          </h1>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
            {/* Name fields */}
            <div className="flex gap-4">
              <div className="w-1/2">
                <label htmlFor="givenName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  First name
                </label>
                <input
                  id="givenName"
                  type="text"
                  className={`mt-1 block w-full rounded-md border px-4 py-3 text-sm shadow-sm focus:border-primary focus:ring-1 focus:ring-primary ${
                    errors.givenName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  {...register('givenName')}
                />
                {errors.givenName && <p className="mt-1 text-xs text-red-600">{errors.givenName.message}</p>}
              </div>

              <div className="w-1/2">
                <label htmlFor="familyName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Last name
                </label>
                <input
                  id="familyName"
                  type="text"
                  className={`mt-1 block w-full rounded-md border px-4 py-3 text-sm shadow-sm focus:border-primary focus:ring-1 focus:ring-primary ${
                    errors.familyName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  {...register('familyName')}
                />
                {errors.familyName && <p className="mt-1 text-xs text-red-600">{errors.familyName.message}</p>}
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Personal email address
              </label>
              <input
                id="email"
                type="email"
                placeholder="you@example.com"
                className={`mt-1 block w-full rounded-md border px-4 py-3 text-sm shadow-sm focus:border-primary focus:ring-1 focus:ring-primary ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                {...register('email')}
              />
              {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
              {plusAddressing && (
                <p className="mt-1 text-xs text-yellow-700 dark:text-yellow-400">
                  Plus-address detected — that’s fine! Remember we only allow one account per primary address (everything before “+” counts as the same inbox).
                </p>
              )}
            </div>

            {/* Helper text */}
            <p className="text-sm text-gray-600 dark:text-gray-400">
              We recommend a <strong>personal</strong> email so your account remains portable if you move between institutions.
            </p>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting || sending}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 font-semibold text-white shadow hover:bg-primary-dark disabled:opacity-60"
            >
              {sending || isSubmitting ? (
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : null}
              {sending || isSubmitting ? 'Sending…' : 'Send code'}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
